import React, { useState, useEffect, useContext, useRef, useMemo, useCallback } from "react";
import classNames from "classnames/bind";
import styles from "./Spinner.module.css";
import modalStyles from "./Modal.module.css";
import { ToastContext } from "~/components/context/ToastContext";
import Info from "~/components/Info";
import Header from "~/components/Header";
import { BASE_LUCKY_URL } from "~/helper/apiService";
import WheelComponent from "./WheelComponent";

const cx = classNames.bind(styles);
const cxModal = classNames.bind(modalStyles);

const BASE_URL = "https://xanh19.com/spin.php/";

// Dữ liệu mặc định cho vòng quay
const DEFAULT_REWARDS = [
  { id: "1", name: "Đang tải" },
  { id: "2", name: "Đang tải" },
  { id: "3", name: "Đang tải" },
  { id: "4", name: "Đang tải" },
  { id: "5", name: "Đang tải" },
  { id: "6", name: "Đang tải" },
  { id: "7", name: "Đang tải" },
  { id: "8", name: "Đang tải" },
];

// Helpers
const normalizeAngle = (deg) => ((deg % 360) + 360) % 360;

const computeSlices = (rewardList) => {
  const count = rewardList.length || 1;
  const sliceSize = 360 / count;
  const slices = rewardList.map((r, i) => {
    const startDeg = i * sliceSize;
    const endDeg = (i + 1) * sliceSize;
    const centerDeg = startDeg + sliceSize / 2;
    return { ...r, index: i, startDeg, endDeg, centerDeg };
  });
  return { slices, sliceSize };
};

// Utility to mask phone number
const maskPhoneNumber = (phone) => {
  return phone.slice(-4).padStart(phone.length, "*");
};

function Spinner() {
  const { showToast } = useContext(ToastContext);
  const [spinning, setSpinning] = useState(false);
  const [spinButtonText, setSpinButtonText] = useState("Quay ngay");
  const [rotation, setRotation] = useState(0);
  const [result, setResult] = useState("");
  const [phone, setPhone] = useState("");
  const [travelDate, setTravelDate] = useState("");
  const [pickupPoint, setPickupPoint] = useState("");
  const [dropoffPoint, setDropoffPoint] = useState("");
  const [rewardId, setRewardId] = useState("");
  const [isPhoneModalOpen, setIsPhoneModalOpen] = useState(false);
  const [isResultModalOpen, setIsResultModalOpen] = useState(false);
  const [status, setStatus] = useState({
    loading: false,
    error: null,
    success: false,
  });
  const [history, setHistory] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [rewards, setRewards] = useState(DEFAULT_REWARDS);
  const [rewardsLoading, setRewardsLoading] = useState(true);
  const [slices, setSlices] = useState([]);
  const [sliceSize, setSliceSize] = useState(45);
  const [mustStartSpinning, setMustStartSpinning] = useState(false);
  const [prizeNumber, setPrizeNumber] = useState(0);
  const [isReadyToSpin, setIsReadyToSpin] = useState(false); // Thêm state
  const hasFetched = useRef(false);

  // Khởi tạo slices với dữ liệu mặc định
  useEffect(() => {
    const { slices: s, sliceSize: size } = computeSlices(rewards);
    setSlices(s);
    setSliceSize(size);
  }, [rewards]);

  // Tải rewards và history từ API chỉ một lần
  useEffect(() => {
    if (hasFetched.current) return;
    hasFetched.current = true;

    const fetchData = async () => {
      try {
        setRewardsLoading(true);
        setHistoryLoading(true);
        const [rewardsRes, historyRes] = await Promise.all([
          fetch(`${BASE_URL}rewards`),
          fetch(`${BASE_URL}history`),
        ]);

        if (!rewardsRes.ok) {
          throw new Error(`HTTP ${rewardsRes.status}: Không lấy được danh sách phần thưởng`);
        }
        if (!historyRes.ok) {
          throw new Error(`HTTP ${historyRes.status}: Không lấy được lịch sử`);
        }

        const rewardsData = await rewardsRes.json();
        const historyData = await historyRes.json();

        setRewards(rewardsData.length > 0 ? rewardsData : DEFAULT_REWARDS);
        setHistory(historyData);
        setIsReadyToSpin(true); // Sẵn sàng quay sau khi tải xong rewards
      } catch (error) {
        console.error("Error fetching data:", error);
        showToast(error.message || "Không thể tải dữ liệu", "error");
        setRewards(DEFAULT_REWARDS);
      } finally {
        setRewardsLoading(false);
        setHistoryLoading(false);
      }
    };

    fetchData();
  }, []);

  // Memoize wheel data to prevent unnecessary re-renders
  const wheelData = useMemo(() => {
    return rewards.map((r) => ({ option: String(r.name || "") }));
  }, [rewards]);

  const validatePhone = (phone) =>
    /^(\+84|84|0)(3[2-9]|5[25689]|7[06-9]|8[1-9]|9[0-46-9])[0-9]{7}$/.test(phone);

  const handlePhoneSubmit = async (e) => {
    e.preventDefault();
    if (!validatePhone(phone)) {
      setStatus({
        loading: false,
        error: "Số điện thoại không hợp lệ!",
        success: false,
      });
      showToast("Số điện thoại không hợp lệ!", "error");
      return;
    }

    setStatus({ loading: true, error: null, success: false });

    try {
      const res = await fetch(`${BASE_URL}check-phone`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ phone }),
      });
      if (!res.ok) {
        const errorData = await res.json();
        setStatus({
          loading: false,
          error: errorData.error || "Số điện thoại đã được sử dụng!",
          success: false,
        });
        showToast(errorData.error || "Số điện thoại đã được sử dụng!", "error");
        return;
      }
      const data = await res.json();
      if (data.can_spin) {
        setTimeout(() => {
          setIsPhoneModalOpen(false);
          setStatus({ loading: false, error: null, success: true });
          showToast("Số điện thoại hợp lệ, bạn có thể quay!", "success");
          setTimeout(() => {
            setIsReadyToSpin(true); // Cho phép quay sau 1s
          }, 1000);
        }, 200); // Tăng trễ lên 200ms
      } else {
        throw new Error("Không thể quay");
      }
    } catch (error) {
      setStatus({
        loading: false,
        error: error.message,
        success: false,
      });
      showToast(error.message || "Không thể kiểm tra số điện thoại", "error");
    }
  };

  const spinWheel = async () => {
    if (spinning || rewardsLoading || !isReadyToSpin) return;

  if (!phone) {
    setIsPhoneModalOpen(true);
    showToast("Vui lòng nhập số điện thoại để quay!", "info");
    return;
  }
    setSpinning(true);
    setResult("");
    setRewardId("");
    setIsResultModalOpen(false);
    setStatus({ loading: false, error: null, success: false });
    setMustStartSpinning(false);

    try {
      const res = await fetch(`${BASE_URL}spin`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ phone }),
      });
      if (!res.ok) {
        const errorData = await res.json();
        setSpinning(false);
        setSpinButtonText("Quay ngay");
        showToast(errorData.error || "Đã hết phần thưởng", "error");
        setStatus({
          loading: false,
          error: errorData.error || "Đã hết phần thưởng",
          success: false,
        });
        return;
      }
      const data = await res.json();
      const targetIndex = rewards.findIndex((r) => r.id === data.id);
      if (targetIndex < 0) {
        throw new Error("Không tìm thấy phần thưởng phù hợp");
      }

      setTimeout(() => {
        setPrizeNumber(targetIndex);
        setRewardId(data.id);
        setResult(data.name);
        setMustStartSpinning(true);
      }, 50);
    } catch (e) {
      setSpinning(false);
      setSpinButtonText("Quay ngay");
      showToast(e.message || "Không thể quay lúc này. Thử lại sau.", "error");
      setStatus({
        loading: false,
        error: e.message,
        success: false,
      });
    }
  };

  const handlePhoneChange = (e) => {
    setPhone(e.target.value);
    if (status.error) {
      setStatus({ ...status, error: null });
    }
  };

  const handleTravelDateChange = (e) => {
    setTravelDate(e.target.value);
    if (status.error) {
      setStatus({ ...status, error: null });
    }
  };

  const handlePickupPointChange = (e) => {
    setPickupPoint(e.target.value);
    if (status.error) {
      setStatus({ ...status, error: null });
    }
  };

  const handleDropoffPointChange = (e) => {
    setDropoffPoint(e.target.value);
    if (status.error) {
      setStatus({ ...status, error: null });
    }
  };

  const handleResultSubmit = async (e) => {
    e.preventDefault();

    if (!travelDate) {
      setStatus({
        loading: false,
        error: "Vui lòng nhập ngày dự kiến đi!",
        success: false,
      });
      showToast("Vui lòng nhập ngày dự kiến đi!", "error");
      return;
    }

    if (!pickupPoint) {
      setStatus({
        loading: false,
        error: "Vui lòng nhập điểm đón!",
        success: false,
      });
      showToast("Vui lòng nhập điểm đón!", "error");
      return;
    }

    if (!dropoffPoint) {
      setStatus({
        loading: false,
        error: "Vui lòng nhập điểm trả!",
        success: false,
      });
      showToast("Vui lòng nhập điểm trả!", "error");
      return;
    }

    if (!rewardId) {
      setStatus({
        loading: false,
        error: "Không có phần thưởng được chọn!",
        success: false,
      });
      showToast("Không có phần thưởng được chọn!", "error");
      return;
    }

    setStatus({ loading: true, error: null, success: false });

    try {
      const res = await fetch(`${BASE_LUCKY_URL}history`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          prize: result,
          phone,
          travel_date: travelDate,
          pickup_point: pickupPoint,
          dropoff_point: dropoffPoint,
          reward_id: rewardId,
        }),
      });
      if (!res.ok) {
        const errorData = await res.json();
        showToast(errorData.error || "Có lỗi xảy ra khi gửi thông tin!", "error");
        throw new Error(errorData.error || "Có lỗi xảy ra khi gửi thông tin!");
      }
      const newEntry = await res.json();
      setHistory((prev) => [newEntry, ...prev].slice(0, 10));
      setStatus({
        loading: false,
        error: null,
        success: true,
      });
      setIsResultModalOpen(false);
      setPhone("");
      setTravelDate("");
      setPickupPoint("");
      setDropoffPoint("");
      setRewardId("");
      showToast("Thông tin của bạn đã được ghi nhận!", "success");
    } catch (error) {
      setStatus({
        loading: false,
        error: error.message,
        success: false,
      });
      showToast(error.message || "Có lỗi xảy ra khi gửi thông tin!", "error");
    }
  };

  const closePhoneModal = () => {
    setIsPhoneModalOpen(false);
    setPhone("");
    setStatus({ loading: false, error: null, success: false });
    // setIsReadyToSpin(false); // Reset khi đóng modal
  };

  const closeResultModal = () => {
    setIsResultModalOpen(false);
    setTravelDate("");
    setPickupPoint("");
    setDropoffPoint("");
    setStatus({ loading: false, error: null, success: false });
  };

  const handleStopSpinning = useCallback(() => {
    const idx = Math.min(Math.max(prizeNumber, 0), rewards.length - 1);
    const landed = rewards[idx];
    setResult(landed ? landed.name : "");
    setSpinning(false);
    setSpinButtonText("Hết lượt");
    setMustStartSpinning(false);
    setIsResultModalOpen(true);
  }, [prizeNumber, rewards]);

  // Debug re-renders của WheelComponent
  useEffect(() => {
    console.log("Wheel props updated:", { mustStartSpinning, prizeNumber, wheelData });
  }, [mustStartSpinning, prizeNumber, wheelData]);

  return (
    <div className={cx("wrapper")}>
      <Header />
      <div className={cx("wheel-container")}>
        <WheelComponent
          mustStartSpinning={mustStartSpinning}
          prizeNumber={Math.min(Math.max(prizeNumber, 0), rewards.length - 1)}
          wheelData={wheelData}
          onStopSpinning={handleStopSpinning}
        />
        <div
          className={cx("inner-circle", {
            disabled: spinning || rewardsLoading || spinButtonText === "Hết lượt" || !isReadyToSpin,
          })}
          onClick={spinWheel}
        >
          <span className={cx("inner-circle-text")}>
            {spinning
              ? "Đang quay..."
              : rewardsLoading
              ? "Đang tải..."
              : !isReadyToSpin
              ? "Đang sẵn sàng..."
              : spinButtonText}
          </span>
        </div>
      </div>

      {isPhoneModalOpen && (
        <div className={cxModal("modal-overlay")}>
          <div className={cxModal("modal")}>
            <button className={cxModal("modal-close")} onClick={closePhoneModal}>
              ✕
            </button>
            <h3 className={cxModal("modal-title")}>Vui lòng nhập số điện thoại</h3>
            <form className={cxModal("form")} onSubmit={handlePhoneSubmit}>
              <div className={cxModal("form-group")}>
                <label htmlFor="phone-input">Số điện thoại</label>
                <input
                  id="phone-input"
                  type="tel"
                  name="phone"
                  value={phone}
                  onChange={handlePhoneChange}
                  placeholder="Số điện thoại"
                  aria-label="Số điện thoại"
                  required
                  pattern="^(\+84|84|0)(3[2-9]|5[25689]|7[06-9]|8[1-9]|9[0-46-9])[0-9]{7}$"
                />
              </div>
              {status.error && (
                <p className={cxModal("error")}>{status.error}</p>
              )}
              <button
                type="submit"
                className={cxModal("submit-btn", { loading: status.loading })}
                disabled={status.loading}
              >
                {status.loading ? (
                  <>
                    <span className={cxModal("loader")}></span>
                    Đang kiểm tra...
                  </>
                ) : (
                  "Xác nhận"
                )}
              </button>
            </form>
          </div>
        </div>
      )}

      {isResultModalOpen && (
        <div className={cxModal("modal-overlay")}>
          <div className={cxModal("modal")}>
            <button className={cxModal("modal-close")} onClick={closeResultModal}>
              ✕
            </button>
            <h3 className={cxModal("modal-title")}>
              {status.success ? "Cảm ơn bạn!" : "Chúc mừng!"}
            </h3>
            <p className={cxModal("modal-content")}>
              {status.success
                ? "Chúng tôi đã ghi nhận thông tin của bạn. Nhân viên sẽ liên hệ để xác nhận."
                : `Bạn đã nhận được: ${result}`}
            </p>
            {!status.success && (
              <form className={cxModal("form")} onSubmit={handleResultSubmit}>
                <div className={cxModal("form-group")}>
                  <label htmlFor="phone-input">Số điện thoại</label>
                  <input
                    id="phone-input"
                    type="tel"
                    name="phone"
                    value={phone}
                    disabled
                    aria-label="Số điện thoại"
                  />
                </div>
                <div className={cxModal("form-group")}>
                  <label htmlFor="travel-date-input">Ngày dự kiến đi</label>
                  <input
                    id="travel-date-input"
                    type="date"
                    name="travelDate"
                    value={travelDate}
                    onChange={handleTravelDateChange}
                    placeholder="Ngày dự kiến đi"
                    aria-label="Ngày dự kiến đi"
                    required
                  />
                </div>
                <div className={cxModal("form-group")}>
                  <label htmlFor="pickup-point-input">Điểm đón</label>
                  <input
                    id="pickup-point-input"
                    type="text"
                    name="pickupPoint"
                    value={pickupPoint}
                    onChange={handlePickupPointChange}
                    placeholder="Điểm đón"
                    aria-label="Điểm đón"
                    required
                  />
                </div>
                <div className={cxModal("form-group")}>
                  <label htmlFor="dropoff-point-input">Điểm trả</label>
                  <input
                    id="dropoff-point-input"
                    type="text"
                    name="dropoffPoint"
                    value={dropoffPoint}
                    onChange={handleDropoffPointChange}
                    placeholder="Điểm trả"
                    aria-label="Điểm trả"
                    required
                  />
                </div>
                {status.error && (
                  <p className={cxModal("error")}>{status.error}</p>
                )}
                <button
                  type="submit"
                  className={cxModal("submit-btn", { loading: status.loading })}
                  disabled={status.loading}
                >
                  {status.loading ? (
                    <>
                      <span className={cxModal("loader")}></span>
                      Đang xử lý...
                    </>
                  ) : (
                    "Gửi"
                  )}
                </button>
              </form>
            )}
          </div>
        </div>
      )}

      <div className={cx("history-container")}>
        <h3 className={cx("history-title")}>Lịch sử quay thưởng gần đây</h3>
        {historyLoading ? (
          <p>Đang tải lịch sử...</p>
        ) : history.length === 0 ? (
          <p>Chưa có lịch sử quay thưởng.</p>
        ) : (
          <ul className={cx("history-list")}>
            {history.map((entry) => (
              <li key={entry.id} className={cx("history-item")}>
                <span className={cx("prize")}>{entry.prize}</span>
                <span className={cx("phone")}>{maskPhoneNumber(entry.phone)}</span>
                <span className={cx("timestamp")}>
                  {new Date(entry.timestamp).toLocaleString("vi-VN", {
                    dateStyle: "short",
                    timeStyle: "short",
                  })}
                </span>
              </li>
            ))}
          </ul>
        )}
      </div>

      <Info />
    </div>
  );
}

export default Spinner;